import { useCallback, useEffect, useState } from 'react'
import {
  adminErrorMessage, copyToClipboard, isAmbiguousFailure,
  issueTeacherCode, listTeachers, revokeTeacher, voidTeacherCode,
} from '../../lib/adminTeachers'

// Teacher management. Everything here is authorised on the server; this
// component only draws what it is told and asks before anything destructive.
//
// The issued code lives in state and nowhere else. It is shown once, with a
// copy button that reports whether the copy actually worked, and is cleared by
// an explicit acknowledgement. Nothing can bring it back.

const BLANK = { label: '', organization: '', referralId: '', teacherEmail: '', note: '' }

// Below this the seven-column table stops being readable at any column width,
// so the rows become cards instead. A media query rather than a width
// constant, so it follows the device rather than a guess about it.
const NARROW = '(max-width: 860px)'

function useNarrow() {
  const [narrow, setNarrow] = useState(
    () => (typeof matchMedia === 'function' ? matchMedia(NARROW).matches : false),
  )
  useEffect(() => {
    if (typeof matchMedia !== 'function') return undefined
    const mq = matchMedia(NARROW)
    const update = (e) => setNarrow(e.matches)
    mq.addEventListener('change', update)
    setNarrow(mq.matches)
    return () => mq.removeEventListener('change', update)
  }, [])
  return narrow
}

export default function TeachersTab() {
  const [teachers, setTeachers] = useState([])
  const [redeemUrl, setRedeemUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const [form, setForm] = useState(BLANK)
  const [showForm, setShowForm] = useState(false)
  const [issuing, setIssuing] = useState(false)
  const [issued, setIssued] = useState(null)       // { code, referralId, studentLink }
  const [copied, setCopied] = useState(null)       // null | true | false
  const [confirm, setConfirm] = useState(null)     // { kind, row }
  const [busyRow, setBusyRow] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const narrow = useNarrow()

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await listTeachers()
      setTeachers(data.teachers || [])
      setRedeemUrl(data.redeemUrl || '')
    } catch (err) {
      setError(adminErrorMessage(err))
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function submitIssue(event) {
    event.preventDefault()
    if (issuing) return
    setIssuing(true)
    setError('')
    try {
      const result = await issueTeacherCode(form)
      setIssued(result)
      setCopied(null)
      setForm(BLANK)
      setShowForm(false)
      await load()
    } catch (err) {
      // Never retry automatically. On an ambiguous failure the code may exist
      // despite the error, and a second attempt would leave an orphan nobody
      // has seen — so reload and let the administrator look.
      if (isAmbiguousFailure(err)) {
        // Reload FIRST: load() clears the error banner as it starts, so a
        // message set before it would be wiped — leaving a silently reloaded
        // list at the one moment an explanation matters most.
        await load()
        setError('The result is unclear. The list has been reloaded — check whether the code was issued before trying again.')
      } else {
        setError(adminErrorMessage(err))
      }
    }
    setIssuing(false)
  }

  async function act() {
    const { kind, row } = confirm
    setConfirm(null)
    setBusyRow(row.codeHash || row.uid)
    setError('')
    try {
      if (kind === 'void') await voidTeacherCode(row.codeHash)
      else await revokeTeacher(row.uid, row.label)
      await load()
    } catch (err) {
      setError(adminErrorMessage(err))
    }
    setBusyRow(null)
  }

  const visible = teachers.filter(t => {
    if (statusFilter === 'active' && t.access !== 'active') return false
    if (statusFilter === 'issued' && t.status !== 'issued') return false
    if (statusFilter === 'revoked' && t.access !== 'revoked') return false
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return [t.label, t.organization, t.referralId, t.teacherEmail, t.redeemedByEmail]
      .some(v => String(v || '').toLowerCase().includes(q))
  })

  return (
    <div className="admin-teachers">
      {/* The code, once. */}
      {issued && (
        <div className="teacher-code-once" role="alert">
          <h4>Code issued — this is the only time it will be shown</h4>
          <input className="teacher-code-once-value" readOnly value={issued.code}
            onFocus={e => e.target.select()} aria-label="Teacher code" />
          <div className="teacher-code-once-actions">
            <button onClick={async () => setCopied(await copyToClipboard(issued.code))}>Copy code</button>
            <button className="primary" onClick={() => { setIssued(null); setCopied(null) }}>
              I have saved this code
            </button>
          </div>
          {copied === true && <p className="ok">Copied.</p>}
          {copied === false && <p className="warn">Copy failed — select the code above and copy it manually.</p>}
          <p className="teacher-code-once-note">
            Send it privately. It cannot be shown again; a lost code must be voided and reissued.<br />
            Teacher redeems at <code>{redeemUrl}</code><br />
            Student link <code>{issued.studentLink}</code>
          </p>
        </div>
      )}

      {error && <p className="admin-error" role="alert">{error}</p>}

      <div className="admin-teachers-bar">
        <input className="admin-search" placeholder="Search name, parish, referral or email"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status">
          <option value="all">All</option>
          <option value="active">Active access</option>
          <option value="issued">Not yet redeemed</option>
          <option value="revoked">Revoked</option>
        </select>
        <button onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button className="primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Issue a code'}
        </button>
      </div>

      {showForm && (
        <form className="admin-issue-form" onSubmit={submitIssue}>
          <label>Teacher<input required value={form.label} placeholder="Deacon Nathan Mekonnen"
            onChange={e => setForm({ ...form, label: e.target.value })} /></label>
          <label>Parish or organisation<input value={form.organization} placeholder="St Mary — Houston"
            onChange={e => setForm({ ...form, organization: e.target.value })} /></label>
          <label>Referral id<input required value={form.referralId} placeholder="teacher-nathan"
            onChange={e => setForm({ ...form, referralId: e.target.value.toLowerCase() })} /></label>
          <label>Their email (for your records)<input type="email" value={form.teacherEmail}
            placeholder="nathan@example.com"
            onChange={e => setForm({ ...form, teacherEmail: e.target.value })} /></label>
          <p className="admin-hint">
            The email is a record only. The code works for whoever holds it, so send it privately.
          </p>
          <button className="primary" type="submit" disabled={issuing}>
            {issuing ? 'Issuing…' : 'Issue code'}
          </button>
        </form>
      )}

      {loading ? <p>Loading teachers…</p> : visible.length === 0 ? (
        <p className="admin-empty">{teachers.length ? 'No teachers match that search.' : 'No codes issued yet.'}</p>
      ) : narrow ? (
        <ul className="teacher-cards">
          {visible.map(t => {
            const key = t.codeHash || t.uid || t.referralId
            const open = expanded === key
            return (
              <li key={key} className="teacher-card">
                <button className="teacher-card-head" aria-expanded={open}
                  onClick={() => setExpanded(open ? null : key)}>
                  <span className="teacher-card-name" title={t.label}>{t.label}</span>
                  {t.organization && <span className="teacher-card-parish">{t.organization}</span>}
                  <span className="teacher-card-pills">
                    <span className={'admin-pill status-' + t.status}>{t.status}</span>
                    <span className={'admin-pill access-' + t.access}>{t.access}</span>
                  </span>
                  <svg className={`teacher-card-chevron ${open ? 'open' : ''}`} width="18" height="18"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                {open && (
                  <div className="teacher-card-detail">
                    <dl>
                      <dt>Referral</dt><dd><code>{t.referralId}</code></dd>
                      <dt>Redeemed by</dt>
                      <dd>{t.redeemedByEmail || t.uid || (t.teacherEmail ? t.teacherEmail + ' (intended)' : '—')}</dd>
                      <dt>Issued</dt><dd>{(t.issuedAt || '').slice(0, 10) || '—'}</dd>
                      <dt>Redeemed</dt><dd>{(t.redeemedAt || '').slice(0, 10) || '—'}</dd>
                    </dl>
                    <div className="teacher-card-actions">
                      {t.status === 'issued' && (
                        <button disabled={busyRow === t.codeHash}
                          onClick={() => setConfirm({ kind: 'void', row: t })}>Void code</button>
                      )}
                      {t.access === 'active' && (
                        <button className="danger" disabled={busyRow === t.uid}
                          onClick={() => setConfirm({ kind: 'revoke', row: t })}>Revoke access</button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <table className="admin-table admin-teachers-table">
          <colgroup>
            <col className="col-teacher" /><col className="col-parish" /><col className="col-referral" />
            <col className="col-status" /><col className="col-redeemed" /><col className="col-access" />
            <col className="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th>Teacher</th><th>Parish</th><th>Referral</th><th>Status</th>
              <th>Redeemed by</th><th>Access</th><th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(t => (
              <tr key={t.codeHash || t.uid || t.referralId}>
                <td className="cell-strong" title={t.label}>{t.label}</td>
                <td title={t.organization}>{t.organization || '—'}</td>
                <td><code title={t.referralId}>{t.referralId}</code></td>
                <td><span className={'admin-pill status-' + t.status}>{t.status}</span></td>
                <td title={t.redeemedByEmail || t.uid || t.teacherEmail || ''}>
                  {t.redeemedByEmail || t.uid || (t.teacherEmail ? t.teacherEmail + ' (intended)' : '—')}
                </td>
                <td><span className={'admin-pill access-' + t.access}>{t.access}</span></td>
                <td className="cell-actions">
                  {t.status === 'issued' && (
                    <button disabled={busyRow === t.codeHash}
                      onClick={() => setConfirm({ kind: 'void', row: t })}>Void</button>
                  )}
                  {t.access === 'active' && (
                    <button className="danger" disabled={busyRow === t.uid}
                      onClick={() => setConfirm({ kind: 'revoke', row: t })}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm && (
        <div className="upgrade-overlay" onClick={() => setConfirm(null)}>
          <div className="upgrade-prompt admin-confirm" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            {confirm.kind === 'void' ? (
              <>
                <h3 className="upgrade-title">Void this code?</h3>
                <p className="teacher-code-note">
                  <strong>{confirm.row.label}</strong> — the code can no longer be redeemed.
                  Use this for a code that was lost, never arrived, or went to the wrong person.
                </p>
              </>
            ) : (
              <>
                <h3 className="upgrade-title">Revoke this teacher?</h3>
                <p className="teacher-code-note">
                  <strong>{confirm.row.label}</strong>
                  {confirm.row.redeemedByEmail ? ` (${confirm.row.redeemedByEmail})` : ''} loses
                  Premium immediately. The code is already spent and cannot be reused, so restoring
                  access means issuing a new one. Any subscription they pay for themselves is unaffected.
                </p>
              </>
            )}
            <button className="upgrade-cta" onClick={act}>
              {confirm.kind === 'void' ? 'Void the code' : 'Revoke access'}
            </button>
            <button className="upgrade-secondary" onClick={() => setConfirm(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
